// What agents write in the folder: `.modelviewer/sketches/*.efmodel.json` and `.modelviewer/notes/*.md`.
// They appear as read-only sketches and notes, and update live. A file that is half written or
// invalid keeps its last good version on screen, and its problem is listed.
import { noteTitleOf, parseDocument, type DiscoveryResult, type EfModelDocument } from '@modelviewer/core';
import { create } from 'zustand';
import type { NoteRecord, SketchBase, SketchRecord } from '../lib/db';
import { toast } from './toast';
import { dropSketch, putSketch, registerFolderSync, useWorkspace } from './workspace';

const SKETCH = /^\.modelviewer\/sketches\/[^/]+\.efmodel\.json$/;
const NOTE = /^\.modelviewer\/notes\/[^/]+\.md$/;

export const isFolderSketch = (path: string) => SKETCH.test(path);
export const isFolderNote = (path: string) => NOTE.test(path);
export const folderId = (path: string) => `file:${path}`;

/** Files in `.modelviewer/` that could not be read, with the reason. */
export const useFolderProblems = create<Record<string, string>>(() => ({}));

/** The model a document says it is based on, if this folder has it. */
export function baseFromDoc(doc: EfModelDocument, discovery: DiscoveryResult | null, sketches: Record<string, SketchRecord>): { setKey: string | null; base: SketchBase | null } {
  const b = doc.basedOn;
  const set = discovery?.sets.find((s) => s.contextName === b?.context) ?? (discovery?.sets.length === 1 && !b?.context ? discovery.sets[0] : undefined);
  if (b?.sketch) {
    const parent = Object.values(sketches).find((s) => s.doc.title === b.sketch);
    if (parent) return { setKey: parent.setKey, base: { setKey: parent.setKey ?? '', migrationId: null, sketchId: parent.id } };
  }
  if (!set) return { setKey: null, base: null };
  const migration = b?.migration ? set.migrations.find((m) => m.id === b.migration || m.name === b.migration || m.id.endsWith(`_${b.migration}`)) : undefined;
  return { setKey: set.key, base: { setKey: set.key, migrationId: migration?.id ?? null } };
}

registerFolderSync(async (source, files, discovery, change) => {
  const relevant = (p: string) => SKETCH.test(p) || NOTE.test(p);
  const toRead = change ? [...change.added, ...change.changed].filter(relevant) : files.map((f) => f.path).filter(relevant);
  const problems = { ...useFolderProblems.getState() };

  for (const path of change?.removed.filter(relevant) ?? []) {
    delete problems[path];
    if (SKETCH.test(path)) dropSketch(folderId(path));
    else
      useWorkspace.setState((s) => {
        const notes = { ...s.notes };
        delete notes[folderId(path)];
        return { notes };
      });
  }

  for (const path of toRead) {
    let text: string;
    try {
      text = await source.read(path);
    } catch {
      continue;
    }
    const id = folderId(path);
    const state = useWorkspace.getState();
    if (SKETCH.test(path)) {
      const result = parseDocument(text);
      if (!result.ok || !result.document) {
        const first = result.issues.find((i) => i.severity === 'error');
        problems[path] = first ? `${first.path ? `${first.path}: ` : ''}${first.message}` : 'Not an efmodel document.';
        continue;
      }
      delete problems[path];
      const existing = state.sketches[id];
      const { setKey, base } = baseFromDoc(result.document, discovery, state.sketches);
      const now = Date.now();
      const record: SketchRecord = { id, sourceId: source.id, setKey, base, doc: result.document, created: existing?.created ?? now, updated: now, origin: { path } };
      putSketch(record);
      if (change) toast(`${existing ? 'Updated' : 'New'} sketch from the folder: ${result.document.title}`);
    } else {
      delete problems[path];
      const existing = state.notes[id];
      const now = Date.now();
      const note: NoteRecord = { id, sourceId: source.id, title: noteTitleOf(path, text), body: text, created: existing?.created ?? now, updated: now, origin: { path } };
      useWorkspace.setState((s) => ({ notes: { ...s.notes, [id]: note } }));
      if (change) toast(`${existing ? 'Updated' : 'New'} note from the folder: ${note.title}`);
    }
  }
  useFolderProblems.setState(problems, true);
});

// Opening another folder starts over.
useWorkspace.subscribe((state, previous) => {
  if (state.source !== previous.source) useFolderProblems.setState({}, true);
});
